import * as React from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import { api } from '../../../util/api'

const getJobSearch =
  ({ paramString, runWalkthrough = false }) =>
  async ({ pageParam }) => {
    const basePath = `?page=${pageParam}&${paramString}`
    const path = runWalkthrough ? basePath + '&from_walkthrough=true' : basePath
    const response = await api.get(path)
    return response.data
  }

export function useJobSearchQuery({
  paramString,
  runWalkthrough = false,
  initialData,
}) {
  const [lastInitialData, setLastInitialData] = React.useState(initialData)
  const [pages, setPages] = React.useState([])
  const [jobResults, setJobResults] = React.useState([])
  const [pagesToShow, setPagesToShow] = React.useState(1)

  const query = useInfiniteQuery({
    queryKey: ['job-search', paramString],
    queryFn: getJobSearch({ paramString, runWalkthrough }),
    initialData: lastInitialData,
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages) => lastPage.next_page,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const {
    dataUpdatedAt,
    error,
    errorUpdatedAt,
    failureCount,
    isError,
    isFetched,
    isFetchedAfterMount,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isPending,
    isIdle,
    isLoading,
    isPlaceholderData,
    isPreviousData,
    isStale,
    isSuccess,
    status,
    updatedAt,
    fetchNextPage,
  } = query

  const queryDataPages = query.data?.pages
  const queryData = query.data
  const queryDataPagesLength = queryDataPages?.length || 0
  const pagesLength = pages?.length || 0
  const pagesLastTotalResults = pages[pages.length - 1]?.total_results || 0
  const pagesLastNextPage = pages[pages.length - 1]?.next_page || 0
  const pagesLastTotalPages = pages[pages.length - 1]?.total_pages || 0
  const showViewMoreButton = pagesToShow < pagesLastTotalPages
  const jobResultsLength = jobResults?.length || 0
  const amountShown = jobResults?.length || 0
  const amountTotal = pagesLastTotalResults || 0

  const queryStatusMessage = JSON.stringify(
    {
      pagesToShow,
      queryDataPagesLength,
      pagesLength,
      hasNextPage,
      jobResultsLength,
      amountShown,
      amountTotal,
      pagesLastTotalResults,
      pagesLastNextPage,
      pagesLastTotalPages,
      showViewMoreButton,
      isFetched,
      isFetchedAfterMount,
      isFetching,
      isFetchingNextPage,
      isPending,
      isIdle,
      isLoading,
      isPreviousData,
      isStale,
      isSuccess,
      status,
      updatedAt,
      dataUpdatedAt,
      isPlaceholderData,
      error,
      errorUpdatedAt,
      failureCount,
      isError,
    },
    null,
    2,
  )
  const debug = false
  if (debug) console.log('query status: ', queryStatusMessage)

  // Show a few less pages than we have
  React.useEffect(() => {
    if (queryDataPagesLength === 1) {
      setPages(queryDataPages)
    } else if (queryDataPagesLength > 1) {
      setPages(queryDataPages?.slice(0, pagesToShow))
    }
  }, [queryDataPages, pagesToShow, queryDataPagesLength])

  // Set boards and jobs when pages are updated
  React.useEffect(() => {
    const jobs = pages.map((page) => page.results).flat()
    setJobResults(jobs)
  }, [pages])

  React.useEffect(() => {
    setLastInitialData()
  }, [queryData])

  return {
    query,
    queryStatusMessage,
    pagesToShow,
    setPagesToShow,
    jobResults,
    amountShown,
    amountTotal,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    showViewMoreButton,
  }
}
